import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Trash2, Plus, MessageSquare, Send } from 'lucide-react';
import { reservationsApi, Room } from '../api/reservations';
import { roomsApi } from '../api/rooms';
import { cleaningApi, CleaningRecord } from '../api/cleaning';
import { messagingApi, SendMessageRequest } from '../api/messaging';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { format, addDays, differenceInDays } from 'date-fns';
import { ko } from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// ISO 시간 문자열 또는 HH:MM 형식에서 HH:MM 추출
function extractTimeString(timeValue: string | null | undefined): string {
  if (!timeValue) return '';

  // 이미 HH:MM 형식인 경우 (예: "15:00")
  if (/^\d{1,2}:\d{2}$/.test(timeValue)) {
    return timeValue;
  }

  // HH:MM:SS 형식인 경우 (예: "15:00:00") - 초 제거
  if (/^\d{1,2}:\d{2}:\d{2}$/.test(timeValue)) {
    return timeValue.substring(0, 5);
  }

  // ISO 날짜 문자열인 경우 (예: "1970-01-01T15:00:00.000Z")
  if (timeValue.includes('T')) {
    try {
      const date = new Date(timeValue);
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch {
      return '';
    }
  }

  return timeValue;
}

export default function EditReservationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [roomId, setRoomId] = useState('');
  const [checkinDate, setCheckinDate] = useState<Date | null>(null);
  const [checkoutDate, setCheckoutDate] = useState<Date | null>(null);
  const [checkinTime, setCheckinTime] = useState('');
  const [checkoutTime, setCheckoutTime] = useState('');
  const [sellingPrice, setSellingPrice] = useState(0);
  const [receivedPrice, setReceivedPrice] = useState(0);
  const [checkinMemo, setCheckinMemo] = useState('');
  const [checkoutMemo, setCheckoutMemo] = useState('');
  const [adminMemo, setAdminMemo] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [status, setStatus] = useState<'confirmed' | 'cancelled'>('confirmed');
  const [lateCheckout12, setLateCheckout12] = useState(false);
  const [lateCheckout1, setLateCheckout1] = useState(false);
  const [selectedJaesilDate, setSelectedJaesilDate] = useState<Date | null>(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [availableRooms, setAvailableRooms] = useState<Room[]>([]);
  const [originalValues, setOriginalValues] = useState<Record<string, any>>({});
  const [isMessagingOpen, setIsMessagingOpen] = useState(false);

  // 토스트 자동 숨김 (메모리 누수 방지를 위한 cleanup 포함)
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // 토스트 알림 표시
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
  };

  // Fetch reservation
  const { data: reservation, isLoading, error, refetch } = useQuery({
    queryKey: ['reservation', id],
    queryFn: () => reservationsApi.getReservationById(id!),
    enabled: !!id,
  });

  // Fetch all rooms for dropdown
  const { data: rooms = [] } = useQuery({
    queryKey: ['rooms'],
    queryFn: () => roomsApi.getAllRooms(),
  });

  // Initialize form when reservation loads
  useEffect(() => {
    if (reservation) {
      const guestNameVal = reservation.guestName || '';
      const guestPhoneVal = reservation.guestPhone || '';
      const roomIdVal = reservation.roomId || '';
      const checkinDateVal = new Date(reservation.checkinDate);
      const checkoutDateVal = new Date(reservation.checkoutDate);
      const checkinTimeVal = extractTimeString(reservation.checkinTime);
      const checkoutTimeVal = extractTimeString(reservation.checkoutTime);
      const sellingPriceVal = reservation.sellingPrice || 0;
      const receivedPriceVal = reservation.receivedPrice || 0;
      const checkinMemoVal = reservation.checkinMemo || '';
      const checkoutMemoVal = reservation.checkoutMemo || '';
      const adminMemoVal = reservation.adminMemo || '';
      const specialRequestsVal = reservation.specialRequests || '';
      const statusVal = reservation.status === 'cancelled' ? 'cancelled' : 'confirmed';

      // 레이트체크아웃 체크박스 초기화 (캘린더 모달과 동일)
      const timeStr = reservation.lateCheckoutTime || '';
      const memo = reservation.checkoutMemo || '';

      let lateCheckout12Val = false;
      let lateCheckout1Val = false;
      if (reservation.isLateCheckout) {
        lateCheckout12Val = timeStr.includes('12:00') || timeStr.includes('T12:');
        lateCheckout1Val = timeStr.includes('13:00') || timeStr.includes('T13:');
      } else {
        lateCheckout12Val = memo.includes('레이트12시');
        lateCheckout1Val = memo.includes('레이트1시');
      }

      // Set form state
      setGuestName(guestNameVal);
      setGuestPhone(guestPhoneVal);
      setRoomId(roomIdVal);
      setCheckinDate(checkinDateVal);
      setCheckoutDate(checkoutDateVal);
      setCheckinTime(checkinTimeVal);
      setCheckoutTime(checkoutTimeVal);
      setSellingPrice(sellingPriceVal);
      setReceivedPrice(receivedPriceVal);
      setCheckinMemo(checkinMemoVal);
      setCheckoutMemo(checkoutMemoVal);
      setAdminMemo(adminMemoVal);
      setSpecialRequests(specialRequestsVal);
      setStatus(statusVal);
      setLateCheckout12(lateCheckout12Val);
      setLateCheckout1(lateCheckout1Val);

      // Store original values for change tracking
      setOriginalValues({
        guestName: guestNameVal,
        guestPhone: guestPhoneVal,
        roomId: roomIdVal,
        checkinDate: format(checkinDateVal, 'yyyy-MM-dd'),
        checkoutDate: format(checkoutDateVal, 'yyyy-MM-dd'),
        checkinTime: checkinTimeVal,
        checkoutTime: checkoutTimeVal,
        sellingPrice: sellingPriceVal,
        receivedPrice: receivedPriceVal,
        checkinMemo: checkinMemoVal,
        checkoutMemo: checkoutMemoVal,
        adminMemo: adminMemoVal,
        specialRequests: specialRequestsVal,
        status: statusVal,
        lateCheckout12: lateCheckout12Val,
        lateCheckout1: lateCheckout1Val,
      });
    }
  }, [reservation]);

  // 체크인 날짜 변경 시 가용 객실 조회
  useEffect(() => {
    let isMounted = true;

    if (checkinDate) {
      const dateStr = format(checkinDate, 'yyyy-MM-dd');
      reservationsApi.getAvailableRooms(dateStr)
        .then(data => {
          if (isMounted) setAvailableRooms(data);
        })
        .catch(err => {
          console.error('Failed to fetch available rooms:', err);
          if (isMounted) setAvailableRooms([]);
        });
    }

    return () => { isMounted = false; };
  }, [checkinDate]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: any) => reservationsApi.updateReservation(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation', id] });
      // 캘린더 및 대시보드 캐시도 무효화
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['calendarDuplicates'] });
      queryClient.invalidateQueries({ queryKey: ['activeReservations'] });
      queryClient.invalidateQueries({ queryKey: ['availableRooms'] });
      queryClient.invalidateQueries({ queryKey: ['cancelledReservations'] });
      refetch();
      showToast('저장되었습니다.');
    },
    onError: (error) => {
      console.error('Update failed:', error);
      showToast('저장에 실패했습니다.', 'error');
    },
  });

  // 메모 정리 함수
  const cleanupMemo = (memo: string): string => {
    return memo
      .replace(/,\s*,+/g, ',')  // 연속 쉼표 제거
      .replace(/^[\s,]+/, '')    // 앞쪽 쉼표/공백 제거
      .replace(/[\s,]+$/, '')    // 뒤쪽 쉼표/공백 제거
      .trim();
  };

  // 레이트체크아웃 체크박스 변경 처리 (캘린더 모달과 동일)
  const handleLateCheckout12Change = (checked: boolean) => {
    setLateCheckout12(checked);
    if (checked) {
      setLateCheckout1(false);
      let newMemo = checkoutMemo.replace(/레이트1시/g, '');
      newMemo = cleanupMemo(newMemo);
      if (!newMemo.includes('레이트12시')) {
        newMemo = newMemo ? `${newMemo}, 레이트12시` : '레이트12시';
      }
      setCheckoutMemo(cleanupMemo(newMemo));
    } else {
      const newMemo = checkoutMemo.replace(/레이트12시/g, '');
      setCheckoutMemo(cleanupMemo(newMemo));
    }
  };

  // 체크인 시간 변경 처리 - 메모에 (C/I=xx:00) 업데이트
  const handleCheckinTimeChange = (newTime: string) => {
    setCheckinTime(newTime);
    if (newTime) {
      // 기존 (C/I=xx:00) 패턴 제거 후 새로운 시간 추가
      let newMemo = checkinMemo.replace(/\(C\/I=\d{1,2}:\d{2}\)/g, '');
      newMemo = cleanupMemo(newMemo);
      const timeTag = `(C/I=${newTime})`;
      newMemo = newMemo ? `${timeTag}, ${newMemo}` : timeTag;
      setCheckinMemo(cleanupMemo(newMemo));
    }
  };

  // 체크아웃 시간 변경 처리 - 메모에 (C/O=xx:00) 업데이트
  const handleCheckoutTimeChange = (newTime: string) => {
    setCheckoutTime(newTime);
    if (newTime) {
      // 기존 (C/O=xx:00) 패턴 제거 후 새로운 시간 추가
      let newMemo = checkoutMemo.replace(/\(C\/O=\d{1,2}:\d{2}\)/g, '');
      newMemo = cleanupMemo(newMemo);
      const timeTag = `(C/O=${newTime})`;
      newMemo = newMemo ? `${timeTag}, ${newMemo}` : timeTag;
      setCheckoutMemo(cleanupMemo(newMemo));
    }
  };

  const handleLateCheckout1Change = (checked: boolean) => {
    setLateCheckout1(checked);
    if (checked) {
      setLateCheckout12(false);
      let newMemo = checkoutMemo.replace(/레이트12시/g, '');
      newMemo = cleanupMemo(newMemo);
      if (!newMemo.includes('레이트1시')) {
        newMemo = newMemo ? `${newMemo}, 레이트1시` : '레이트1시';
      }
      setCheckoutMemo(cleanupMemo(newMemo));
    } else {
      const newMemo = checkoutMemo.replace(/레이트1시/g, '');
      setCheckoutMemo(cleanupMemo(newMemo));
    }
  };

  // 연박처리 핸들러 (캘린더 모달과 동일)
  const handleExtendedStay = async () => {
    if (!reservation) return;

    const roomAddress = reservation.room?.roomAddress?.toUpperCase() || reservation.roomId;
    const name = reservation.guestName;

    // 확인 다이얼로그
    if (!window.confirm(`${roomAddress}, ${name} 연박처리하시겠습니까?`)) {
      return;
    }

    setIsSaving(true);
    try {
      // 1. checkinMemo에서 (C/I=xx:00) 패턴을 (C/I=11:00)으로 변경
      let newMemo = checkinMemo.replace(/\(C\/I=\d{1,2}:\d{2}\)/g, '(C/I=11:00)');

      // 2. "❌ 연박(청소취소)" 추가 (중복 방지)
      if (!newMemo.includes('연박(청소취소)')) {
        newMemo = newMemo ? `${newMemo}, ❌ 연박(청소취소)` : '❌ 연박(청소취소)';
      }
      newMemo = cleanupMemo(newMemo);

      // 3. API 호출 - checkinTime과 checkinMemo 업데이트
      await reservationsApi.updateReservation(id!, {
        checkinTime: '11:00',
        checkinMemo: newMemo,
      });

      // 상태 업데이트
      setCheckinTime('11:00');
      setCheckinMemo(newMemo);
      // 캘린더 및 대시보드 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['activeReservations'] });
      queryClient.invalidateQueries({ queryKey: ['availableRooms'] });
      queryClient.invalidateQueries({ queryKey: ['cancelledReservations'] });
      refetch();
      showToast('연박처리 완료');
    } catch (err) {
      console.error('Failed to process extended stay:', err);
      showToast('연박처리 실패', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Clear vehicles mutation
  const clearVehiclesMutation = useMutation({
    mutationFn: () => reservationsApi.clearVehicles(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation', id] });
      // 캘린더 및 대시보드 캐시도 무효화
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['activeReservations'] });
      queryClient.invalidateQueries({ queryKey: ['availableRooms'] });
      queryClient.invalidateQueries({ queryKey: ['cancelledReservations'] });
      refetch();
      alert('차량 정보가 삭제되었습니다.');
    },
    onError: (error) => {
      console.error('Clear vehicles failed:', error);
      alert('차량 정보 삭제에 실패했습니다.');
    },
  });

  // Fetch cleaning records for the reservation period
  const { data: cleaningRecords = [], refetch: refetchCleaning } = useQuery({
    queryKey: ['cleaningRecords', reservation?.roomId, reservation?.checkinDate, reservation?.checkoutDate],
    queryFn: () => cleaningApi.getCleaningRecords(
      reservation!.roomId,
      reservation!.checkinDate,
      reservation!.checkoutDate
    ),
    enabled: !!reservation,
  });

  // Jaesil date range (체크인부터 체크아웃 전날까지)
  const jaesilDateRange = useMemo(() => {
    if (!reservation) return { minDate: null, maxDate: null };
    const minDate = new Date(reservation.checkinDate);
    const maxDate = addDays(new Date(reservation.checkoutDate), -1);
    return { minDate, maxDate };
  }, [reservation]);

  // Add jaesil mutation
  const addJaesilMutation = useMutation({
    mutationFn: ({ date }: { date: string }) =>
      cleaningApi.addJaesil(reservation!.roomId, date, '재실청소'),
    onSuccess: () => {
      refetchCleaning();
      showToast('재실청소 추가됨');
    },
    onError: (error) => {
      console.error('Add jaesil failed:', error);
      showToast('재실청소 추가 실패', 'error');
    },
  });

  // Delete jaesil mutation
  const deleteJaesilMutation = useMutation({
    mutationFn: ({ date }: { date: string }) =>
      cleaningApi.deleteJaesil(reservation!.roomId, date),
    onSuccess: () => {
      refetchCleaning();
      showToast('재실청소 삭제됨');
    },
    onError: (error) => {
      console.error('Delete jaesil failed:', error);
      showToast('재실청소 삭제 실패', 'error');
    },
  });

  const handleSave = async () => {
    if (!checkinDate || !checkoutDate) {
      alert('체크인/체크아웃 날짜를 선택해주세요.');
      return;
    }

    setIsSaving(true);
    try {
      const selectedRoom = rooms.find((r: Room) => r.roomId === roomId);

      // 객실이 변경되었으면 먼저 updateRoom 호출 (이력 기록 + 텔레그램)
      if (roomId !== originalValues.roomId) {
        await reservationsApi.updateRoom(id!, roomId);
      }

      // 레이트체크아웃 설정 (캘린더 모달과 동일)
      const isLateCheckout = lateCheckout12 || lateCheckout1;
      let lateCheckoutTime: string | undefined = undefined;
      if (lateCheckout12) {
        lateCheckoutTime = '12:00';
      } else if (lateCheckout1) {
        lateCheckoutTime = '13:00';
      }

      await updateMutation.mutateAsync({
        guestName,
        guestPhone,
        roomId,
        roomType: selectedRoom?.roomType || reservation?.roomType,
        checkinDate: format(checkinDate, 'yyyy-MM-dd'),
        checkoutDate: format(checkoutDate, 'yyyy-MM-dd'),
        checkinTime: checkinTime || undefined,
        checkoutTime: checkoutTime || undefined,
        sellingPrice,
        receivedPrice,
        checkinMemo,
        checkoutMemo,
        adminMemo,
        specialRequests,
        status,
        isLateCheckout,
        lateCheckoutTime: isLateCheckout ? lateCheckoutTime : undefined,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearVehicles = () => {
    if (!reservation?.guestParking?.length) {
      alert('등록된 차량이 없습니다.');
      return;
    }
    if (window.confirm('차량 정보를 모두 삭제하시겠습니까?')) {
      clearVehiclesMutation.mutate();
    }
  };

  const handleAddJaesil = () => {
    if (!selectedJaesilDate) {
      alert('날짜를 선택해주세요.');
      return;
    }
    const dateStr = format(selectedJaesilDate, 'yyyy-MM-dd');
    addJaesilMutation.mutate({ date: dateStr });
    setSelectedJaesilDate(null);
  };

  const handleDeleteJaesil = (date: string) => {
    if (window.confirm(`${date} 재실청소를 삭제하시겠습니까?`)) {
      deleteJaesilMutation.mutate({ date });
    }
  };

  // Filter jaesil records (daily type only)
  const jaesilRecords = useMemo(() => {
    return cleaningRecords.filter((r: CleaningRecord) => r.cleaningType === 'daily');
  }, [cleaningRecords]);

  // 메시지 전송을 위한 기본 데이터 생성
  const createMessageRequest = (
    messageType: 'confirm' | 'checkin' | 'checkout' | 'review',
    isScheduled: boolean,
    scheduledTime?: string
  ): SendMessageRequest => {
    // PHP처럼 YYYY-MM-DD 형식으로 전송 (백엔드에서 포맷팅)
    const checkinDateStr = checkinDate ? format(checkinDate, 'yyyy-MM-dd') : '';
    const checkoutDateStr = checkoutDate ? format(checkoutDate, 'yyyy-MM-dd') : '';
    const selectedRoom = rooms.find((r: Room) => r.roomId === roomId);

    return {
      reservationId: reservation!.reservationId,
      phone: guestPhone,
      guestName: guestName,
      messageType,
      isScheduled,
      scheduledTime,
      templateVariables: {
        guestName,
        roomType: selectedRoom?.roomType || reservation?.roomType || '',
        checkinDate: checkinDateStr,
        checkoutDate: checkoutDateStr,
        checkinTime: checkinTime || '15:00',
        checkoutTime: checkoutTime || '11:00',
        reservationId: reservation!.reservationId,
        roomAddress: selectedRoom?.roomAddress || '',
        bookingLink: `https://mdstay.kr/${reservation!.reservationId}`,
        parkingLink: `https://parking.modoostay.co.kr/${reservation!.reservationId}`,
      },
    };
  };

  // SMS 전송 핸들러
  const handleSendSms = async (
    messageType: 'confirm' | 'checkin' | 'checkout' | 'review',
    isScheduled: boolean
  ) => {
    if (!guestPhone) {
      showToast('연락처가 없습니다.', 'error');
      return;
    }

    const typeLabel = {
      confirm: '예약확정',
      checkin: '체크인',
      checkout: '체크아웃',
      review: '리뷰요청',
    }[messageType];

    setIsSendingMessage(true);
    try {
      let scheduledTime: string | undefined;
      if (isScheduled) {
        if (messageType === 'checkin' && checkinDate) {
          scheduledTime = format(checkinDate, 'yyyy-MM-dd') + ' 12:00:00';
        } else if (messageType === 'checkout' && checkoutDate) {
          scheduledTime = format(checkoutDate, 'yyyy-MM-dd') + ' 09:00:00';
        }
      }

      // 예약 전송(체크인/체크아웃)인 경우, 기존 메시지 먼저 취소 (LMS + 카카오 둘 다)
      if (isScheduled && (messageType === 'checkin' || messageType === 'checkout')) {
        try {
          await messagingApi.cancelSms({
            reservationId: reservation!.reservationId,
            messageType,
            channel: 'sms',
          });
        } catch {
          // 취소할 메시지가 없어도 무시하고 진행
        }
      }

      await messagingApi.sendSms(createMessageRequest(messageType, isScheduled, scheduledTime));
      showToast(`${typeLabel} SMS ${isScheduled ? '예약됨' : '전송됨'}`);
    } catch (error: any) {
      console.error('SMS send failed:', error);
      showToast(`SMS 전송 실패`, 'error');
    } finally {
      setIsSendingMessage(false);
    }
  };

  // 카카오톡 전송 핸들러
  const handleSendKakao = async (
    messageType: 'confirm' | 'checkin' | 'checkout',
    isScheduled: boolean
  ) => {
    if (!guestPhone) {
      showToast('연락처가 없습니다.', 'error');
      return;
    }

    const typeLabel = {
      confirm: '예약확정',
      checkin: '체크인',
      checkout: '체크아웃',
    }[messageType];

    setIsSendingMessage(true);
    try {
      let scheduledTime: string | undefined;
      if (isScheduled) {
        if (messageType === 'checkin' && checkinDate) {
          scheduledTime = format(checkinDate, 'yyyy-MM-dd') + ' 12:00:00';
        } else if (messageType === 'checkout' && checkoutDate) {
          scheduledTime = format(checkoutDate, 'yyyy-MM-dd') + ' 09:00:00';
        }
      }

      // 예약 전송(체크인/체크아웃)인 경우, 기존 메시지 먼저 취소 (LMS + 카카오 둘 다)
      if (isScheduled && (messageType === 'checkin' || messageType === 'checkout')) {
        try {
          await messagingApi.cancelKakao({
            reservationId: reservation!.reservationId,
            messageType,
            channel: 'kakao',
          });
        } catch {
          // 취소할 메시지가 없어도 무시하고 진행
        }
      }

      await messagingApi.sendKakao(createMessageRequest(messageType, isScheduled, scheduledTime));
      showToast(`${typeLabel} 카카오톡 ${isScheduled ? '예약됨' : '전송됨'}`);
    } catch (error: any) {
      console.error('Kakao send failed:', error);
      showToast('카카오톡 전송 실패', 'error');
    } finally {
      setIsSendingMessage(false);
    }
  };

  // SMS 취소 핸들러
  const handleCancelSms = async (messageType: 'checkin' | 'checkout' | 'review') => {
    const typeLabel = {
      checkin: '체크인',
      checkout: '체크아웃',
      review: '리뷰요청',
    }[messageType];

    setIsSendingMessage(true);
    try {
      const result = await messagingApi.cancelSms({
        reservationId: reservation!.reservationId,
        messageType,
        channel: 'sms',
      });
      // 백엔드에서 반환한 메시지 표시 (SMS 취소됨, 카카오톡 취소됨, 둘 다 등)
      showToast(`${typeLabel}: ${result.data?.message || '취소 완료'}`);
    } catch (error: any) {
      console.error('SMS cancel failed:', error);
      showToast('취소 실패', 'error');
    } finally {
      setIsSendingMessage(false);
    }
  };

  // 카카오톡 취소 핸들러
  const handleCancelKakao = async (messageType: 'checkin' | 'checkout' | 'review') => {
    const typeLabel = {
      checkin: '체크인',
      checkout: '체크아웃',
      review: '리뷰',
    }[messageType];

    setIsSendingMessage(true);
    try {
      const result = await messagingApi.cancelKakao({
        reservationId: reservation!.reservationId,
        messageType,
        channel: 'kakao',
      });
      // 백엔드에서 반환한 메시지 표시 (SMS 취소됨, 카카오톡 취소됨, 둘 다 등)
      showToast(`${typeLabel}: ${result.data?.message || '취소 완료'}`);
    } catch (error: any) {
      console.error('Kakao cancel failed:', error);
      showToast('취소 실패', 'error');
    } finally {
      setIsSendingMessage(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner className="py-16" />;
  }

  if (error || !reservation) {
    return (
      <div className="text-center py-16">
        <p className="text-red-500">예약을 찾을 수 없습니다.</p>
        <button onClick={() => navigate('/dashboard')} className="btn btn-primary mt-4">
          대시보드로 이동
        </button>
      </div>
    );
  }

  // 변경된 필드 계산
  const getChangedFields = () => {
    if (!originalValues || Object.keys(originalValues).length === 0) return [];

    const changes: { label: string; oldValue: string; newValue: string }[] = [];

    // 객실 변경 시 roomAddress로 표시
    const getOriginalRoomAddress = () => {
      const room = rooms.find((r: Room) => r.roomId === originalValues.roomId);
      return room?.roomAddress?.toUpperCase() || originalValues.roomId;
    };
    const getNewRoomAddress = () => {
      const room = rooms.find((r: Room) => r.roomId === roomId);
      return room?.roomAddress?.toUpperCase() || roomId;
    };

    if (guestName !== originalValues.guestName) {
      changes.push({ label: '예약자명', oldValue: originalValues.guestName, newValue: guestName });
    }
    if (guestPhone !== originalValues.guestPhone) {
      changes.push({ label: '연락처', oldValue: originalValues.guestPhone, newValue: guestPhone });
    }
    if (roomId !== originalValues.roomId) {
      changes.push({ label: '객실', oldValue: getOriginalRoomAddress(), newValue: getNewRoomAddress() });
    }
    if (checkinDate && format(checkinDate, 'yyyy-MM-dd') !== originalValues.checkinDate) {
      changes.push({ label: '체크인', oldValue: originalValues.checkinDate, newValue: format(checkinDate, 'yyyy-MM-dd') });
    }
    if (checkoutDate && format(checkoutDate, 'yyyy-MM-dd') !== originalValues.checkoutDate) {
      changes.push({ label: '체크아웃', oldValue: originalValues.checkoutDate, newValue: format(checkoutDate, 'yyyy-MM-dd') });
    }
    if (checkinTime !== originalValues.checkinTime) {
      changes.push({ label: '체크인시간', oldValue: originalValues.checkinTime || '-', newValue: checkinTime || '-' });
    }
    if (checkoutTime !== originalValues.checkoutTime) {
      changes.push({ label: '체크아웃시간', oldValue: originalValues.checkoutTime || '-', newValue: checkoutTime || '-' });
    }
    if (sellingPrice !== originalValues.sellingPrice) {
      changes.push({ label: '판매가', oldValue: originalValues.sellingPrice?.toLocaleString() || '0', newValue: sellingPrice.toLocaleString() });
    }
    if (receivedPrice !== originalValues.receivedPrice) {
      changes.push({ label: '입금가', oldValue: originalValues.receivedPrice?.toLocaleString() || '0', newValue: receivedPrice.toLocaleString() });
    }
    if (status !== originalValues.status) {
      const statusLabel = { confirmed: '확정', cancelled: '취소' };
      changes.push({ label: '상태', oldValue: statusLabel[originalValues.status as keyof typeof statusLabel] || originalValues.status, newValue: statusLabel[status] });
    }
    if (lateCheckout12 !== originalValues.lateCheckout12) {
      changes.push({ label: '레이트12시', oldValue: originalValues.lateCheckout12 ? 'O' : 'X', newValue: lateCheckout12 ? 'O' : 'X' });
    }
    if (lateCheckout1 !== originalValues.lateCheckout1) {
      changes.push({ label: '레이트1시', oldValue: originalValues.lateCheckout1 ? 'O' : 'X', newValue: lateCheckout1 ? 'O' : 'X' });
    }
    if (checkinMemo !== originalValues.checkinMemo) {
      changes.push({ label: '체크인메모', oldValue: originalValues.checkinMemo || '-', newValue: checkinMemo || '-' });
    }
    if (checkoutMemo !== originalValues.checkoutMemo) {
      changes.push({ label: '체크아웃메모', oldValue: originalValues.checkoutMemo || '-', newValue: checkoutMemo || '-' });
    }
    if (specialRequests !== originalValues.specialRequests) {
      changes.push({ label: '게스트 특별요청', oldValue: originalValues.specialRequests || '-', newValue: specialRequests || '-' });
    }
    if (adminMemo !== originalValues.adminMemo) {
      changes.push({ label: '관리자메모', oldValue: originalValues.adminMemo || '-', newValue: adminMemo || '-' });
    }

    return changes;
  };

  const changedFields = getChangedFields();

  // 캘린더 모달과 동일: roomType → roomId 순 정렬, none 제외, available만
  const sortedRooms = [...rooms]
    .filter((r: Room) => r.status === 'available' && r.roomId !== 'none')
    .sort((a: Room, b: Room) => {
      // roomType 먼저 비교
      const typeCompare = (a.roomType || '').localeCompare(b.roomType || '');
      if (typeCompare !== 0) return typeCompare;
      // 같은 타입이면 roomId로 비교
      return (a.roomId || '').localeCompare(b.roomId || '');
    });

  // 타입별 그룹핑
  const groupedRooms = sortedRooms.reduce((acc, room) => {
    const type = room.roomType || 'Other';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(room);
    return acc;
  }, {} as Record<string, Room[]>);

  // 타입 목록 (알파벳순 정렬)
  const roomTypes = Object.keys(groupedRooms).sort((a, b) => a.localeCompare(b));

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">예약 상세</h1>
        </div>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          disabled={isSaving}
        >
          <Save className="w-4 h-4" />
          {isSaving ? '업데이트 중...' : '업데이트'}
        </button>
      </div>

      {/* 수정된 내용 표시 */}
      {changedFields.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="text-sm text-yellow-800">
            <span className="font-medium">수정된 항목 ({changedFields.length}개):</span>
            <div className="mt-1 space-y-1">
              {changedFields.map((change, idx) => (
                <div key={idx} className="flex flex-wrap gap-1">
                  <span className="font-medium">{change.label}:</span>
                  <span className="text-yellow-600">{change.oldValue}</span>
                  <span>→</span>
                  <span className="text-green-700 font-medium">{change.newValue}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SMS / 카카오톡 */}
      <div className="card p-6 bg-white rounded-lg shadow">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setIsMessagingOpen(!isMessagingOpen)}
        >
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">SMS / 카카오톡</h2>
          </div>
          <span className="text-gray-500">{isMessagingOpen ? '▲' : '▼'}</span>
        </div>
        {isMessagingOpen && (
          <>
        <h3 className="text-sm font-medium text-gray-700 mt-4 mb-2">SMS</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleSendSms('confirm', false)}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 disabled:opacity-50"
          >
            <Send className="w-3 h-3" />
            ⚡ 예약확정
          </button>
          <button
            onClick={() => handleSendSms('checkin', false)}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 disabled:opacity-50"
          >
            <Send className="w-3 h-3" />
            ⚡ 체크인
          </button>
          <button
            onClick={() => handleSendSms('checkin', true)}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 disabled:opacity-50"
          >
            ⏰ 체크인
          </button>
          <button
            onClick={() => handleSendSms('checkout', true)}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 disabled:opacity-50"
          >
            ⏰ 체크아웃
          </button>
          <button
            onClick={() => handleCancelSms('checkin')}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 disabled:opacity-50"
          >
            ⏰ 체크인취소
          </button>
          <button
            onClick={() => handleCancelSms('checkout')}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 disabled:opacity-50"
          >
            ⏰ 체크아웃취소
          </button>
          <button
            onClick={() => handleCancelSms('review')}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 disabled:opacity-50"
          >
            ⏰ 리뷰요청취소
          </button>
        </div>

        <h3 className="text-sm font-medium text-gray-700 mt-4 mb-2">카카오톡</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleSendKakao('confirm', false)}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 disabled:opacity-50"
          >
            <Send className="w-3 h-3" />
            ⚡ 예약확정
          </button>
          <button
            onClick={() => handleSendKakao('checkin', false)}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 disabled:opacity-50"
          >
            <Send className="w-3 h-3" />
            ⚡ 체크인
          </button>
          <button
            onClick={() => handleSendKakao('checkin', true)}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 disabled:opacity-50"
          >
            ⏰ 체크인
          </button>
          <button
            onClick={() => handleSendKakao('checkout', true)}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 disabled:opacity-50"
          >
            ⏰ 체크아웃
          </button>
          <button
            onClick={() => handleCancelKakao('checkin')}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 disabled:opacity-50"
          >
            ⏰ 체크인취소
          </button>
          <button
            onClick={() => handleCancelKakao('checkout')}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 disabled:opacity-50"
          >
            ⏰ 체크아웃취소
          </button>
          <button
            onClick={() => handleCancelKakao('review')}
            disabled={isSendingMessage}
            className="flex items-center gap-1 px-3 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 disabled:opacity-50"
          >
            ⏰ 리뷰취소
          </button>
        </div>
          </>
        )}
      </div>

      {/* Reservation Info */}
      <div className="card p-6 bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">예약 정보</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-gray-500">예약번호</span>
            <p className="font-medium">{reservation.reservationId}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">상태</span>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={status === 'confirmed'}
                  onChange={() => setStatus('confirmed')}
                />
                확정
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={status === 'cancelled'}
                  onChange={() => setStatus('cancelled')}
                />
                취소
              </label>
            </div>
          </div>
          <div>
            <span className="text-sm text-gray-500">플랫폼</span>
            <p className="font-medium">{reservation.platform}</p>
          </div>
        </div>
      </div>

      {/* Guest Info */}
      <div className="card p-6 bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">고객 정보</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-gray-500">예약자명</span>
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <span className="text-sm text-gray-500">연락처</span>
            <input
              type="text"
              value={guestPhone}
              onChange={(e) => setGuestPhone(e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* Room & Dates */}
      <div className="card p-6 bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">객실 및 일정</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-gray-500">객실</span>
            <select
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
            >
              {roomTypes.map((type) => (
                <optgroup key={type} label={type}>
                  {groupedRooms[type].map((room: Room) => {
                    const isAvailable = availableRooms.some(r => r.roomId === room.roomId);
                    const isCurrent = room.roomId === reservation?.roomId;
                    return (
                      <option key={room.roomId} value={room.roomId}>
                        {isAvailable || isCurrent ? '* ' : ''}{room.roomAddress.toUpperCase()}
                      </option>
                    );
                  })}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <span className="text-sm text-gray-500">객실 타입</span>
            <p className="font-medium">{reservation.roomType}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">객실 비밀번호</span>
            <p className="font-medium">{reservation.room?.password || '-'}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">레이트 체크아웃</span>
            <div className="flex gap-2 mt-2">
              <label
                className={`flex items-center gap-1.5 px-2.5 py-1.5 md:px-4 md:py-2 rounded-lg cursor-pointer border-2 transition-all ${
                  lateCheckout12
                    ? 'bg-orange-100 border-orange-500 text-orange-700'
                    : 'bg-gray-50 border-gray-200 hover:border-orange-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={lateCheckout12}
                  onChange={(e) => handleLateCheckout12Change(e.target.checked)}
                  className="w-4 h-4 accent-orange-500"
                />
                <span className="font-medium text-sm md:text-base">12시</span>
              </label>
              <label
                className={`flex items-center gap-1.5 px-2.5 py-1.5 md:px-4 md:py-2 rounded-lg cursor-pointer border-2 transition-all ${
                  lateCheckout1
                    ? 'bg-red-100 border-red-500 text-red-700'
                    : 'bg-gray-50 border-gray-200 hover:border-red-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={lateCheckout1}
                  onChange={(e) => handleLateCheckout1Change(e.target.checked)}
                  className="w-4 h-4 accent-red-500"
                />
                <span className="font-medium text-sm md:text-base">1시</span>
              </label>
            </div>
          </div>
          <div>
            <span className="text-sm text-gray-500">체크인 날짜</span>
            <DatePicker
              selected={checkinDate}
              onChange={(date) => setCheckinDate(date)}
              dateFormat="yyyy년 M월 d일 (E)"
              locale={ko}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <span className="text-sm text-gray-500">체크인 시간</span>
            <select
              value={checkinTime}
              onChange={(e) => handleCheckinTimeChange(e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
            >
              <option value="">선택</option>
              {/* 10:00 ~ 18:00 */}
              {Array.from({ length: 9 }, (_, i) => {
                const hour = (i + 10).toString().padStart(2, '0');
                return (
                  <option key={hour} value={`${hour}:00`}>
                    {hour}:00
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <span className="text-sm text-gray-500">체크아웃 날짜</span>
            <DatePicker
              selected={checkoutDate}
              onChange={(date) => setCheckoutDate(date)}
              dateFormat="yyyy년 M월 d일 (E)"
              locale={ko}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <span className="text-sm text-gray-500">체크아웃 시간</span>
            <select
              value={checkoutTime}
              onChange={(e) => handleCheckoutTimeChange(e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
            >
              <option value="">선택</option>
              {/* 09:00 ~ 14:00 */}
              {Array.from({ length: 6 }, (_, i) => {
                const hour = (i + 9).toString().padStart(2, '0');
                return (
                  <option key={hour} value={`${hour}:00`}>
                    {hour}:00
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <span className="text-sm text-gray-500">숙박일수</span>
            <p className="font-medium mt-2">
              {checkinDate && checkoutDate
                ? `${differenceInDays(checkoutDate, checkinDate)}박`
                : `${reservation.nights}박`}
            </p>
          </div>
          <div>
            <span className="text-sm text-gray-500">연박처리</span>
            <div className="mt-2">
              <button
                type="button"
                onClick={handleExtendedStay}
                disabled={isSaving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium text-sm md:text-base"
              >
                연박
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="card p-6 bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">가격 정보</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-gray-500">판매가</span>
            <input
              type="text"
              value={sellingPrice.toLocaleString()}
              onChange={(e) => setSellingPrice(parseInt(e.target.value.replace(/,/g, '')) || 0)}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <span className="text-sm text-gray-500">입금가</span>
            <input
              type="text"
              value={receivedPrice.toLocaleString()}
              onChange={(e) => setReceivedPrice(parseInt(e.target.value.replace(/,/g, '')) || 0)}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* Memos */}
      <div className="card p-6 bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">메모</h2>
        <div className="space-y-4">
          <div>
            <span className="text-sm text-gray-500">체크인 특이사항</span>
            <textarea
              value={checkinMemo}
              onChange={(e) => setCheckinMemo(e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
              rows={2}
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {[
                { label: '추가1명', text: '추가1명' },
                { label: '싱글', text: '싱글매트리스' },
                { label: '추가2명', text: '추가2명' },
                { label: '퀸', text: '퀸매트리스' },
                { label: '세탁세제', text: '세탁세제' },
              ].map(({ label, text }) => (
                <button
                  key={label}
                  type="button"
                  className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 border rounded"
                  onClick={() => {
                    if (!checkinMemo.includes(text)) {
                      const newMemo = checkinMemo ? `${checkinMemo}, ${text}` : text;
                      setCheckinMemo(cleanupMemo(newMemo));
                    } else {
                      showToast('이미 추가됨');
                    }
                  }}
                >
                  {label}
                </button>
              ))}
              <button
                type="button"
                className="px-3 py-1 text-xs bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 rounded"
                onClick={() => {
                  let newMemo = checkinMemo;
                  ['추가1명', '추가2명', '퀸매트리스', '싱글매트리스', '세탁세제'].forEach(text => {
                    newMemo = newMemo.replace(new RegExp(text, 'g'), '');
                  });
                  setCheckinMemo(cleanupMemo(newMemo));
                  showToast('초기화됨');
                }}
              >
                초기화
              </button>
            </div>
          </div>
          <div>
            <span className="text-sm text-gray-500">체크아웃 특이사항</span>
            <textarea
              value={checkoutMemo}
              onChange={(e) => setCheckoutMemo(e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
              rows={2}
            />
          </div>
          <div>
            <span className="text-sm text-gray-500">게스트 특별 요청사항</span>
            <textarea
              value={specialRequests}
              onChange={(e) => setSpecialRequests(e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
              rows={2}
            />
          </div>
          <div>
            <span className="text-sm text-gray-500">관리자 메모</span>
            <textarea
              value={adminMemo}
              onChange={(e) => setAdminMemo(e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg"
              rows={2}
            />
          </div>
        </div>
      </div>

      {/* Vehicles */}
      <div className="card p-6 bg-white rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">차량 정보</h2>
          {reservation.guestParking && reservation.guestParking.length > 0 && (
            <button
              onClick={handleClearVehicles}
              className="flex items-center gap-2 px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
            >
              <Trash2 className="w-4 h-4" />
              차량 삭제
            </button>
          )}
        </div>
        {reservation.guestParking && reservation.guestParking.length > 0 ? (
          <div className="space-y-2">
            {reservation.guestParking.map((parking, index) => (
              <div key={parking.id} className="flex items-center gap-2">
                <span className="text-sm text-gray-500">차량 {index + 1}:</span>
                <span className="font-medium">{parking.carNumber}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">등록된 차량이 없습니다.</p>
        )}
      </div>

      {/* Jaesil Cleaning (재실청소) */}
      {reservation.nights > 1 && (
        <div className="card p-6 bg-white rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4">재실청소</h2>

          {/* 날짜 선택 및 추가 */}
          <div className="flex items-end gap-3 mb-4 pb-4 border-b">
            <div>
              <label className="text-sm text-gray-500 block mb-1">청소 날짜</label>
              <DatePicker
                selected={selectedJaesilDate}
                onChange={(date) => setSelectedJaesilDate(date)}
                dateFormat="yyyy-MM-dd"
                minDate={jaesilDateRange.minDate || undefined}
                maxDate={jaesilDateRange.maxDate || undefined}
                placeholderText="날짜 선택"
                className="w-40 px-3 py-2 border rounded-lg"
              />
            </div>
            <button
              onClick={handleAddJaesil}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              추가
            </button>
          </div>

          {/* 청소 기록 목록 */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">청소 기록</h3>
            {jaesilRecords.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-2 px-3">날짜</th>
                      <th className="text-left py-2 px-3">상태</th>
                      <th className="text-left py-2 px-3">담당자</th>
                      <th className="text-left py-2 px-3">관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jaesilRecords.map((record: CleaningRecord) => (
                      <tr key={record.id} className="border-b">
                        <td className="py-2 px-3">{record.cleaningDate.substring(0, 10)}</td>
                        <td className="py-2 px-3">
                          {record.isCleaned ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">완료</span>
                          ) : (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">미완료</span>
                          )}
                        </td>
                        <td className="py-2 px-3">{record.cleaner?.name || '-'}</td>
                        <td className="py-2 px-3">
                          <button
                            onClick={() => handleDeleteJaesil(record.cleaningDate.substring(0, 10))}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                          >
                            <Trash2 className="w-3 h-3" />
                            삭제
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">등록된 청소 기록이 없습니다.</p>
            )}
          </div>
        </div>
      )}

      {/* Room Assignment History */}
      {reservation.roomAssignmentHistory && reservation.roomAssignmentHistory.length > 0 && (
        <div className="card p-6 bg-white rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4">객실 변경 이력</h2>
          <div className="space-y-2">
            {reservation.roomAssignmentHistory.map((history: any, index: number) => (
              <div key={index} className="text-sm">
                <span className="text-gray-500">{history.changed_at}:</span>{' '}
                <span className="font-medium">{history.previous_room_id?.toUpperCase()}</span>
                <span className="text-gray-400 mx-2">→</span>
                <span className="font-medium">{history.new_room_id?.toUpperCase()}</span>
                {history.changed_by && history.changed_by !== 'none' && (
                  <span className="text-gray-500 ml-2">({history.changed_by})</span>
                )}
                {history.reason && history.reason !== 'none' && (
                  <span className="text-gray-400 ml-2">- {history.reason}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 객실안내 접속 로그 */}
      {reservation.readCheckinWebpageLog && reservation.readCheckinWebpageLog.length > 0 && (
        <div className="card p-6 bg-white rounded-lg shadow">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold">객실안내 접속 로그</h2>
            {reservation.readCheckinWebpage && (
              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">확인</span>
            )}
          </div>
          <div className="space-y-1">
            {reservation.readCheckinWebpageLog.map((entry, idx) => (
              <div key={idx} className="text-sm text-gray-600">
                {entry.date && format(new Date(entry.date), 'M월 d일 HH:mm', { locale: ko })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 주차등록 로그 */}
      {(reservation.readParkingWebpageLog?.length || reservation.parkingRegistrationLog?.length) ? (
        <div className="card p-6 bg-white rounded-lg shadow">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold">주차등록 로그</h2>
            {reservation.readParkingWebpage && (
              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">확인</span>
            )}
          </div>
          <div className="space-y-1">
            {reservation.readParkingWebpageLog?.map((entry, idx) => (
              <div key={`parking-view-${idx}`} className="text-sm">
                <span className="text-gray-600">
                  {entry.date && format(new Date(entry.date), 'M월 d일 HH:mm', { locale: ko })}
                </span>
                <span className="ml-2 text-blue-600">페이지 접속</span>
              </div>
            ))}
            {reservation.parkingRegistrationLog?.map((entry, idx) => (
              <div key={`parking-reg-${idx}`} className="text-sm">
                <span className="text-gray-600">
                  {entry.date && format(new Date(entry.date), 'M월 d일 HH:mm', { locale: ko })}
                </span>
                <span className="ml-2 text-green-600">차량 등록</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Toast 알림 */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-white z-50 transition-all ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
